import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    inject,
    Input,
    OnChanges,
    output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FiInfiniteScrollDataSource, TSortOrder } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { MatSortDirection, ObjectTypes } from '@fi-shared/types';
import { getObjectTypeNameTextId, isDefined, isEqual } from '@fi-shared/utils';
import { IChecklist2ReportChecklistSummary } from '../../../../shared/fi-checklist2-report';
import { IChecklistStatusProgressData } from '../../../../../checklist-v2';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';

@Component({
    selector: 'fi-history-checklists-summary-table',
    templateUrl: './checklists-summary-table.component.html',
    styleUrls: ['./checklists-summary-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-checklists-summary-table' },
    standalone: false,
})
export class FiHistoryChecklistsSummaryTable
    extends FiInfiniteScrollDataSource<IChecklist2ReportChecklistSummary>
    implements OnChanges
{
    @Input() checklistsSummary: IChecklist2ReportChecklistSummary[] = [];
    @Input() isTableItemSelectionPrevented: boolean;
    @HostBinding('class.table-item-selection-prevented') get tableItemSelectionPreventedStatus() {
        return this.isTableItemSelectionPrevented;
    }
    public onShowChecklistHistoryDetails = output<IChecklist2ReportChecklistSummary>();

    protected getRowClassFn = (): string => {
        return this.isTableItemSelectionPrevented ? '' : 'cursor-pointer';
    };

    public readonly defaultSortColumnName: string = 'ChecklistName';
    public readonly itemsListType: FiItemsListType = FiItemsListType.ChecklistReportHistoryChecklistsTable;
    public readonly objectType: ObjectTypes = ObjectTypes.Checklist2;

    private appTextService = inject(AppTextService);
    private fiChecklist2ReportHelperService = inject(FiChecklist2ReportHelperService);

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.checklistsSummary;
        if (isDefined(change) && !isEqual(change.previousValue, change.currentValue)) {
            this.reloadData();
        }
    }

    protected showChecklistHistoryDetails(checklistSummary: IChecklist2ReportChecklistSummary): void {
        this.onShowChecklistHistoryDetails.emit(checklistSummary);
    }

    public getListData(): Promise<IChecklist2ReportChecklistSummary[]> {
        return Promise.resolve(this.checklistsSummary);
    }

    protected getChecklistStatusData(item: IChecklist2ReportChecklistSummary): IChecklistStatusProgressData {
        return {
            completedCount: item.CompletedIterationsCount,
            inProgressCount: item.InProgressIterationsCount,
            notStartedCount: item.NotStartedIterationsCount,
        };
    }

    protected showNonComplianceColumn(): boolean {
        return !!this.items().find((item) => item.IsComplianceThresholdEnabled);
    }

    protected getChecklistIterationWithUnitsCount(checklistData: IChecklist2ReportChecklistSummary): string {
        const periodsCount: number = checklistData.PeriodsCount;
        const timeTextId: string = periodsCount === 1 ? 'common.time_counter' : 'common.times_counter';
        const unitsCount: number = checklistData.AllUniqueUnitIds.length;

        return this.appTextService.get('checklist.iterated_and_shared_with_units', {
            '{0}': periodsCount + ' ' + this.appTextService.get(timeTextId),
            '{1}':
                unitsCount +
                ' ' +
                this.appTextService
                    .get(getObjectTypeNameTextId(ObjectTypes.BusinessUnit, unitsCount !== 1))
                    .toLowerCase(),
        });
    }

    protected isNonCompliancePercentageVisible(item: IChecklist2ReportChecklistSummary): boolean {
        return item.NonComplianceIterationsCount > 0 || item.ComplianceIterationsCount > 0;
    }

    protected getNonCompliancePercentage(item: IChecklist2ReportChecklistSummary): number {
        if (item.IsComplianceThresholdEnabled) {
            return this.fiChecklist2ReportHelperService.getNonCompliancePercentage(item);
        }
        return -1;
    }

    protected getNonCompliancePercentageText(item: IChecklist2ReportChecklistSummary): string {
        return this.fiChecklist2ReportHelperService.getNonCompliancePercentageText(item);
    }

    protected getCompliancePercentage(item: IChecklist2ReportChecklistSummary): number {
        return this.fiChecklist2ReportHelperService.getCompliancePercentage(item);
    }

    protected getCompliancePercentageText(item: IChecklist2ReportChecklistSummary): string {
        return this.fiChecklist2ReportHelperService.getCompliancePercentageText(item);
    }

    private readonly complianceIterationsCountColumnName: string = 'NonComplianceIterations';

    protected getSortOrderFn(): TSortOrder<IChecklist2ReportChecklistSummary> {
        if (this.selectedSort.active === this.complianceIterationsCountColumnName)
            return this.getNonCompliancePercentage.bind(this);

        return super.getSortOrderFn();
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly MatSortDirection = MatSortDirection;
}
